import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../api/config';
import { getTradeableAssets, clearAssetsCache } from '../services/tradeableAssetsService';
import { getCortexAnalytics } from '../services/cortexAnalytics';

// Create axios instance with base URL from centralized config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AuthContext = createContext();

// Refresh token queue — prevents infinite 401 loops and parallel refresh storms
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginTimestampRef = useRef(null);

  // Get stored tokens
  const getStoredTokens = useCallback(() => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return { accessToken, refreshToken };
  }, []);

  // Store tokens
  const storeTokens = useCallback((accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }, []);

  // Clear stored tokens
  const clearTokens = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, []);

  const setAuthHeader = useCallback((token) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  const clearAuthHeader = useCallback(() => {
    delete api.defaults.headers.common['Authorization'];
  }, []);

  // Fetch user profile using existing token
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }, []);

  // Refresh session using refresh token
  const refreshSession = useCallback(async () => {
    const { refreshToken } = getStoredTokens();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { user: userData, session: newSession } = response.data;

      if (newSession) {
        storeTokens(newSession.access_token, newSession.refresh_token);
        setAuthHeader(newSession.access_token);
        setSession(newSession);
        setUser(userData);
        return newSession;
      }

      return null;
    } catch (error) {
      console.error('Session refresh failed:', error);
      clearTokens();
      clearAuthHeader();
      return null;
    }
  }, [getStoredTokens, storeTokens, setAuthHeader, clearTokens, clearAuthHeader]);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const { accessToken } = getStoredTokens();

      if (!accessToken) {
        setLoading(false);
        return;
      }

      setAuthHeader(accessToken);

      try {
        // Verify session is still valid
        const response = await api.get('/auth/session');

        if (response.data.session) {
          setSession({ access_token: accessToken });
          await fetchUserProfile();
          // Preload tradeable assets into browser cache (fire-and-forget)
          getTradeableAssets().catch(() => {});
          // Preload Cortex Analytics cache (fire-and-forget)
          getCortexAnalytics(false).catch(() => {});
        } else {
          // Try to refresh the session
          const newSession = await refreshSession();
          if (!newSession) {
            clearTokens();
            clearAuthHeader();
          }
        }
      } catch (error) {
        console.error('[AuthContext] Auth initialization error:', error);
        // Try to refresh on error
        const newSession = await refreshSession();
        if (!newSession) {
          clearTokens();
          clearAuthHeader();
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [getStoredTokens, setAuthHeader, fetchUserProfile, refreshSession, clearTokens, clearAuthHeader]);

  // Set up axios interceptor for token refresh on 401
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Never retry refresh requests — this prevents the infinite 401 loop
        if (originalRequest.url?.includes('/auth/refresh')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          // If a refresh is already in progress, queue this request
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return api(originalRequest);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newSession = await refreshSession();
            if (newSession) {
              processQueue(null, newSession.access_token);
              originalRequest.headers['Authorization'] = `Bearer ${newSession.access_token}`;
              return api(originalRequest);
            }

            // Refresh failed — clear auth state and reject queued requests
            processQueue(new Error('Refresh failed'));
            clearTokens();
            clearAuthHeader();
            setUser(null);
            setSession(null);
          } catch (refreshError) {
            processQueue(refreshError);
            clearTokens();
            clearAuthHeader();
            setUser(null);
            setSession(null);
          } finally {
            isRefreshing = false;
          }
        }

        // Handle 403/429 upgrade-required responses
        if (
          (error.response?.status === 403 || error.response?.status === 429) &&
          error.response?.data?.upgrade_required
        ) {
          window.dispatchEvent(new CustomEvent('upgrade-required', {
            detail: error.response.data,
          }));
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [refreshSession, clearTokens, clearAuthHeader]);

  // Identify user in PostHog whenever auth state resolves or plan changes
  useEffect(() => {
    if (user?.id) {
      posthog.identify(user.id, {
        email: user.email,
        username: user.username,
        plan_id: user.subscription?.plan_id || 'free',
        subscription_status: user.subscription?.status || 'active',
        billing_interval: user.subscription?.billing_interval || null,
        email_domain: user.email ? user.email.split('@')[1] : null,
        created_at: user.created_at || null,
      });
    }
  }, [user?.id, user?.subscription?.plan_id, user?.subscription?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const signup = async (email, password, username) => {
    try {
      const response = await api.post('/auth/signup', {
        email,
        password,
        username,
      });

      const { user: userData, session: newSession } = response.data;

      if (newSession) {
        storeTokens(newSession.access_token, newSession.refresh_token);
        setAuthHeader(newSession.access_token);
        setSession(newSession);
        setUser(userData);
      }

      // Return user data with session info so frontend knows if email confirmation is required
      return {
        ...userData,
        session: newSession || null,
      };
    } catch (error) {
      console.error('Signup error:', error);
      throw new Error(error.response?.data?.error || 'Failed to sign up');
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      const { user: userData, session: newSession } = response.data;

      if (newSession) {
        storeTokens(newSession.access_token, newSession.refresh_token);
        setAuthHeader(newSession.access_token);
        setSession(newSession);
        setUser(userData);
        loginTimestampRef.current = Date.now();
      }

      return userData;
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password');
      }
      throw new Error(error.response?.data?.error || 'Failed to log in');
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if server logout fails
    } finally {
      // Clear ALL auth state completely
      clearTokens();
      clearAuthHeader();
      clearAssetsCache();
      setUser(null);
      setSession(null);

      // Additional safety: clear any other cached data that might leak between users
      sessionStorage.clear();

      // Ensure axios interceptors are reset
      isRefreshing = false;
      failedQueue = [];

      // Reset PostHog identity so next user gets a fresh anonymous session
      posthog.reset();
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw new Error(error.response?.data?.error || 'Failed to update profile');
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      await api.post('/auth/update-password', {
        newPassword,
      });
    } catch (error) {
      console.error('Update password error:', error);
      throw new Error(error.response?.data?.error || 'Failed to update password');
    }
  };

  const loginWithGoogle = async () => {
    try {
      const response = await api.get('/auth/oauth/google', {
        params: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Google OAuth error:', error);
      throw new Error(error.response?.data?.error || 'Failed to start Google sign-in');
    }
  };

  const handleOAuthCallback = async (accessToken, refreshToken) => {
    storeTokens(accessToken, refreshToken);
    setAuthHeader(accessToken);

    try {
      const response = await api.get('/auth/session');
      if (response.data.session) {
        setSession({ access_token: accessToken });
        setUser(response.data.session.user);
        return true;
      }
      clearTokens();
      clearAuthHeader();
      return false;
    } catch (error) {
      console.error('OAuth callback error:', error);
      clearTokens();
      clearAuthHeader();
      return false;
    }
  };

  const deleteAccount = async () => {
    await api.delete('/auth/account');
    clearTokens();
    clearAuthHeader();
    clearAssetsCache();
    setUser(null);
    setSession(null);
    sessionStorage.clear();
    isRefreshing = false;
    failedQueue = [];
    posthog.reset();
  };

  const resetPassword = async (email) => {
    try {
      await api.post('/auth/reset-password', {
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch (error) {
      console.error('Reset password error:', error);
      throw new Error(error.response?.data?.error || 'Failed to send reset password email');
    }
  };

  // Refresh subscription data (call after checkout or plan change)
  const refreshSubscription = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      return null;
    }
  }, []);

  // Get the current session's access token (for external use)
  const getAccessToken = () => {
    return session?.access_token || localStorage.getItem(ACCESS_TOKEN_KEY) || null;
  };

  const value = {
    user,
    session,
    loading,
    signup,
    login,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    updateProfile,
    updatePassword,
    deleteAccount,
    resetPassword,
    getAccessToken,
    refreshSubscription,
    loginTimestampRef,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
